// Aggregation model for the Explorer "Architecture" view. Folds a (potentially
// huge) Customer Knowledge Graph into a small, bounded topology:
//
//   code bases (repositories)      +      cloud providers
//                                          └─ accounts
//                                              └─ resource *types* (aggregated)
//
// The whole point is scale-invariance: 10 EC2 instances collapse to one "EC2 ×10"
// group, so the node count is `codeBases + providers×accounts×types` regardless of
// whether the domain holds five resources or fifty thousand. Pure and
// side-effect free so it can be unit-tested and memoised on the graph reference.

import type { KnowledgeGraph } from '@lizrd/contracts';
import { CLOUD_PROVIDERS, nearestRepo } from './graph-visuals';

type GraphNode = KnowledgeGraph['nodes'][number];

/** Operational metrics we surface, read defensively from `properties.metrics`. */
export interface ResourceMetrics {
  cpuPct: number | null;
  memPct: number | null;
}

/** One aggregated resource type within an account (e.g. "EC2 ×10"). */
export interface TypeGroup {
  /** Stable id: `${accountKey}|${type}`. */
  key: string;
  type: string;
  /** The provider of this account's resources — drives the display label. */
  provider: string;
  count: number;
  totalCost: number;
  /** The underlying resources, sorted by monthly cost (desc). */
  resources: GraphNode[];
  /** Cost-unweighted averages across resources that report the metric. */
  avgCpuPct: number | null;
  avgMemPct: number | null;
}

/** One cloud account (a `cloud.account` node, or a synthetic bucket). */
export interface AccountGroup {
  /** Stable id — the account node id, or a synthetic `${provider}|~unassigned`. */
  key: string;
  accountId: string | null;
  name: string;
  /** The backing `cloud.account` node, or null for a synthetic bucket. */
  node: GraphNode | null;
  totalCost: number;
  resourceCount: number;
  types: TypeGroup[];
}

/** One cloud provider (AWS/GCP/Azure) — a border grouping its accounts. */
export interface ProviderGroup {
  provider: string;
  totalCost: number;
  resourceCount: number;
  accounts: AccountGroup[];
}

/** One aggregated cloud resource type a code base runs. Keyed by provider+type
 * so an S3 bucket and a Cloud Storage bucket stay distinct rows. */
export interface CodeRun {
  type: string;
  provider: string;
  count: number;
  totalCost: number;
}

/** A code base (repository) with what it runs in the cloud, attributed via the
 * same nearest-repository walk used for cost attribution. */
export interface CodeBase {
  node: GraphNode;
  id: string;
  name: string;
  /** Distinct account keys this code base has resources in. */
  ownedAccountKeys: string[];
  /** Cloud resource types it owns, aggregated, sorted by cost (desc). */
  runs: CodeRun[];
  runsCount: number;
  runsCost: number;
}

export interface ArchitectureModel {
  codeBases: CodeBase[];
  providers: ProviderGroup[];
  totalCost: number;
}

const UNASSIGNED = '~unassigned';

const costOf = (n: GraphNode): number =>
  typeof n.properties.monthlyCost === 'number' ? n.properties.monthlyCost : 0;

/** Read CPU/memory utilisation from a node's `properties.metrics`, defensively. */
export function metricsOf(n: GraphNode): ResourceMetrics {
  const m = (n.properties.metrics ?? {}) as Record<string, unknown>;
  const num = (v: unknown): number | null => (typeof v === 'number' ? v : null);
  return { cpuPct: num(m.cpuPct), memPct: num(m.memPct) };
}

const avg = (xs: number[]): number | null =>
  xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null;

/**
 * Fold the graph into the aggregated architecture topology.
 *
 * Resource → account assignment prefers explicit `BELONGS_TO` edges (real syncs);
 * when absent (e.g. the dev seed emits none) it falls back to the provider's sole
 * account, or a synthetic "Unassigned" bucket when a provider has several accounts
 * and no edges to disambiguate.
 */
export function buildArchitecture(graph: KnowledgeGraph): ArchitectureModel {
  const isCloud = (n: GraphNode) => CLOUD_PROVIDERS.has(n.provider);

  // Index accounts by node id, grouped per provider.
  const accountNodes = graph.nodes.filter((n) => n.type === 'cloud.account' && isCloud(n));
  const accountsById = new Map(accountNodes.map((n) => [n.id, n] as const));
  const accountsByProvider = new Map<string, GraphNode[]>();
  for (const a of accountNodes) {
    (
      accountsByProvider.get(a.provider) ?? accountsByProvider.set(a.provider, []).get(a.provider)!
    ).push(a);
  }

  // resource node id → owning account node id, from BELONGS_TO edges.
  const belongsTo = new Map<string, string>();
  for (const e of graph.edges) {
    if (e.type !== 'BELONGS_TO') continue;
    if (accountsById.has(e.to)) belongsTo.set(e.from, e.to);
    else if (accountsById.has(e.from)) belongsTo.set(e.to, e.from);
  }

  // The cloud resources we aggregate: any cloud-provider node that isn't the
  // account hub itself.
  const resources = graph.nodes.filter((n) => isCloud(n) && n.type !== 'cloud.account');

  // Resolve each resource to an account key (node id or synthetic bucket).
  const accountKeyOf = (r: GraphNode): string => {
    const linked = belongsTo.get(r.id);
    if (linked && accountsById.has(linked)) return linked;
    const provAccounts = accountsByProvider.get(r.provider) ?? [];
    if (provAccounts.length === 1) return provAccounts[0]!.id;
    return `${r.provider}|${UNASSIGNED}`;
  };
  const resourceAccountKey = new Map<string, string>();
  for (const r of resources) resourceAccountKey.set(r.id, accountKeyOf(r));

  // Group resources by (account key, type).
  type Acc = { node: GraphNode | null; provider: string; types: Map<string, GraphNode[]> };
  const accounts = new Map<string, Acc>();
  const ensureAccount = (key: string, provider: string, node: GraphNode | null): Acc => {
    let acc = accounts.get(key);
    if (!acc) {
      acc = { node, provider, types: new Map() };
      accounts.set(key, acc);
    }
    return acc;
  };
  // Seed with real account nodes so connected-but-empty accounts still show a hub.
  for (const a of accountNodes) ensureAccount(a.id, a.provider, a);
  for (const r of resources) {
    const key = resourceAccountKey.get(r.id)!;
    const acc = ensureAccount(key, r.provider, accountsById.get(key) ?? null);
    (acc.types.get(r.type) ?? acc.types.set(r.type, []).get(r.type)!).push(r);
  }

  // Materialise account groups → provider groups.
  const providers = new Map<string, AccountGroup[]>();
  for (const [key, acc] of accounts) {
    const types: TypeGroup[] = [...acc.types.entries()].map(([type, rs]) => {
      const sorted = [...rs].sort((a, b) => costOf(b) - costOf(a));
      const cpu = sorted.map((n) => metricsOf(n).cpuPct).filter((v): v is number => v != null);
      const mem = sorted.map((n) => metricsOf(n).memPct).filter((v): v is number => v != null);
      return {
        key: `${key}|${type}`,
        type,
        provider: acc.provider,
        count: sorted.length,
        totalCost: sorted.reduce((s, n) => s + costOf(n), 0),
        resources: sorted,
        avgCpuPct: avg(cpu),
        avgMemPct: avg(mem),
      };
    });
    types.sort((a, b) => b.totalCost - a.totalCost || b.count - a.count);
    const resourceCount = types.reduce((s, t) => s + t.count, 0);
    const totalCost = types.reduce((s, t) => s + t.totalCost, 0);
    const isSynthetic = key.endsWith(`|${UNASSIGNED}`);
    const group: AccountGroup = {
      key,
      accountId: acc.node ? accountIdOf(acc.node) : null,
      name: acc.node ? accountName(acc.node) : isSynthetic ? 'Unassigned' : key,
      node: acc.node,
      totalCost,
      resourceCount,
      types,
    };
    (providers.get(acc.provider) ?? providers.set(acc.provider, []).get(acc.provider)!).push(group);
  }

  const providerGroups: ProviderGroup[] = [...providers.entries()].map(([provider, accs]) => {
    accs.sort((a, b) => b.totalCost - a.totalCost || b.resourceCount - a.resourceCount);
    return {
      provider,
      totalCost: accs.reduce((s, a) => s + a.totalCost, 0),
      resourceCount: accs.reduce((s, a) => s + a.resourceCount, 0),
      accounts: accs,
    };
  });
  providerGroups.sort((a, b) => b.totalCost - a.totalCost);

  // Code bases + what they run in the cloud. Attribution uses the same
  // nearest-repository walk as cost attribution, so a code base links to every
  // account it has resources in — not just directly-OWNS'd ones.
  const owningRepo = nearestRepo(graph); // resource node id → owning repo id
  const runsByRepo = new Map<string, Map<string, GraphNode[]>>();
  const accountsByRepo = new Map<string, Set<string>>();
  for (const r of resources) {
    const repoId = owningRepo.get(r.id);
    if (!repoId) continue;
    const key = resourceAccountKey.get(r.id)!;
    (accountsByRepo.get(repoId) ?? accountsByRepo.set(repoId, new Set()).get(repoId)!).add(key);
    const tmap = runsByRepo.get(repoId) ?? runsByRepo.set(repoId, new Map()).get(repoId)!;
    const runKey = `${r.provider}|${r.type}`;
    (tmap.get(runKey) ?? tmap.set(runKey, []).get(runKey)!).push(r);
  }
  const codeBases: CodeBase[] = graph.nodes
    .filter((n) => n.type === 'repository')
    .map((n) => {
      const tmap = runsByRepo.get(n.id) ?? new Map<string, GraphNode[]>();
      const runs: CodeRun[] = [...tmap.values()]
        .map((rs) => ({
          type: rs[0]!.type,
          provider: rs[0]!.provider,
          count: rs.length,
          totalCost: rs.reduce((s, x) => s + costOf(x), 0),
        }))
        .sort((a, b) => b.totalCost - a.totalCost || b.count - a.count);
      return {
        node: n,
        id: n.id,
        name: n.name,
        ownedAccountKeys: [...(accountsByRepo.get(n.id) ?? [])],
        runs,
        runsCount: runs.reduce((s, r) => s + r.count, 0),
        runsCost: runs.reduce((s, r) => s + r.totalCost, 0),
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  const totalCost = providerGroups.reduce((s, p) => s + p.totalCost, 0);
  return { codeBases, providers: providerGroups, totalCost };
}

function accountIdOf(node: GraphNode): string | null {
  const id = node.properties.accountId;
  return typeof id === 'string' ? id : null;
}

/** A short, human label for an account hub — the friendly name minus the noise. */
function accountName(node: GraphNode): string {
  // Names look like "acme-production (123456789012)" or "AWS account 1234…".
  const stripped = node.name.replace(/\s*\(\d{6,}\)\s*$/, '').trim();
  return stripped || accountIdOf(node) || 'Account';
}
